import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PortService } from 'src/app/services/port/port.service';
import { PortModalService } from 'src/app/services/port-modal/port-modal.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-port',
  templateUrl: './add-edit-port.component.html',
  styleUrls: ['./add-edit-port.component.scss']
})
export class AddEditPortComponent extends DestructorComponent implements OnInit {

  @Output() portAdded = new EventEmitter<void>();
  @Output() portUpdated = new EventEmitter<void>();

  portForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedPort: any = null;

  // Dropdown data
  countryList: any[] = [];

  constructor(
    private fb: FormBuilder,
    private _portService: PortService,
    private _portModalService: PortModalService,
    private _globalStateService: GlobalStateService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCountries();
  }

  private initializeForm(): void {
    this.portForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      country_code: ['', [Validators.required]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_port_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const servicePort = this._portModalService.getSelectedPort();
        if (servicePort) {
          this.isEditMode = true;
          this.selectedPort = servicePort;
          this.populateForm(this.selectedPort);
        } else {
          this.isEditMode = false;
          this.selectedPort = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._portModalService.clearSelectedPort();
        this.resetForm();
      });
    }
  }

  private loadCountries(): void {
    // Subscribe to countries from GlobalStateService
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
      })
    );
  }

  private populateForm(port: any): void {
    this.portForm.patchValue({
      name: port.name || '',
      country_code: port.country_code || ''
    });
  }

  private resetForm(): void {
    this.portForm.reset();
    this.isLoading = false;
  }

  // Getter for easy access to form controls
  get name() { return this.portForm.get('name'); }
  get country_code() { return this.portForm.get('country_code'); }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  onSubmit(): void {
    if (this.portForm.invalid) {
      this.markFormGroupTouched(this.portForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      name: this.portForm.get('name')?.value,
      country_code: this.portForm.get('country_code')?.value
    };

    if (this.isEditMode) {
      this.updatePort(formData);
    } else {
      this.addPort(formData);
    }
  }

  private addPort(formData: any): void {
    this.subscriptions.push(
      this._portService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Port added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.portAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add port.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error adding port:', error);
          this.isLoading = false;

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  private updatePort(formData: any): void {
    const portId = this.selectedPort._id || this.selectedPort.id;
    this.subscriptions.push(
      this._portService.UpdateData(portId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Port updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.portUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update port.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error updating port:', error);
          this.isLoading = false;

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_port_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }
}
